"use client";

// Third-party Imports
import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { getId } from "@/utils/id";

// Server Imports
import {
  doGetElearningCategories,
  doGetElearningCoursesManage,
  doGetElearningCourseByIdManage,
  doDeleteElearningLesson,
  doResetElearningLesson,
  doResetElearningLessonForAll,
} from "@/app/server/actions";

export interface ElearningCategoryItem {
  _id?: any; // may be {$oid}
  id?: string;
  name?: string;
  description?: string;
  createdAt?: any; // may be {$date}
}

interface CategoriesState {
  items: ElearningCategoryItem[];
  filtered: ElearningCategoryItem[];
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: any;
  total: number;
  lastErrorToastTime: number;
}

interface LessonsState {
  data: any[];
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
}

interface LessonItemsState {
  data: any[];
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
  currentLessonId: string | null;
}

interface ManageElearningState {
  categories: CategoriesState;
  courses: CategoriesState;
  lessons: LessonsState;
  lessonItems: LessonItemsState;
}

const initialState: ManageElearningState = {
  categories: {
    items: [],
    filtered: [],
    loading: "idle",
    error: null,
    total: 0,
    lastErrorToastTime: 0,
  },
  courses: {
    items: [],
    filtered: [],
    loading: "idle",
    error: null,
    total: 0,
    lastErrorToastTime: 0,
  },
  lessons: {
    data: [],
    loading: "idle",
    error: null,
  },
  lessonItems: {
    data: [],
    loading: "idle",
    error: null,
    currentLessonId: null,
  },
};

export const manageElearningSlice = createSlice({
  name: "manageElearning",
  initialState,
  reducers: {
    filterCategories: (state, action) => {
      const { search } = action.payload || {};
      const cs = current(state.categories);
      const base = cs.filtered || [];

      if (!search || search.trim().length === 0) {
        state.categories.items = base;

        return;
      }

      const s = search.toLowerCase();

      state.categories.items = base.filter((c: ElearningCategoryItem) => {
        const n = (c?.name || "").toLowerCase();
        const d = (c?.description || "").toLowerCase();


        return n.includes(s) || d.includes(s);
      });
    },
    filterCourses: (state, action) => {
      const { search } = action.payload || {};
      const cs = current(state.courses);
      const base = cs.filtered || [];

      if (!search || search.trim().length === 0) {
        state.courses.items = base;

        return;
      }

      const s = search.toLowerCase();

      state.courses.items = base.filter((c: ElearningCategoryItem) => {
        const n = (c?.name || "").toLowerCase();
        const d = (c?.description || "").toLowerCase();


        return n.includes(s) || d.includes(s);
      });
    },
    clearLessons: state => {
      state.lessons = {
        data: [],
        loading: "idle",
        error: null,
      };
    },
    setLessonItems: (state, action) => {
      const items = action.payload.items || [];


      // Sort by position, then by createdAt as fallback
      const sorted = [...items].sort((a: any, b: any) => {
        const posA = a?.position ?? 999999;
        const posB = b?.position ?? 999999;

        if (posA !== posB) return posA - posB;

        // Fallback to createdAt if positions are equal
        const dateA = new Date(a?.createdAt || 0).getTime();
        const dateB = new Date(b?.createdAt || 0).getTime();


        return dateA - dateB;
      });

      state.lessonItems.data = sorted;
      state.lessonItems.currentLessonId = action.payload.lessonId;
    },
    clearLessonItems: state => {
      state.lessonItems = {
        data: [],
        loading: "idle",
        error: null,
        currentLessonId: null,
      };
    },
    updateLessonItemsOrder: (state, action) => {
      state.lessonItems.data = action.payload.items || [];
    },
    addLessonItemOptimistic: (state, action) => {
      state.lessonItems.data.push(action.payload.item);
    },
    updateLessonItemOptimistic: (state, action) => {
      const { itemId, updates } = action.payload;

      const index = state.lessonItems.data.findIndex((item: any) => {
        const candidates = [
          getId(item),
          (item as any)?._id?.$oid,
          (item as any)?._id,
          (item as any)?.id,
          (item as any)?.data_id,
          (item as any)?.dataId,
        ]
          .filter(Boolean)
          .map(v => String(v));


        return candidates.includes(String(itemId));
      });

      if (index !== -1) {
        state.lessonItems.data[index] = { ...state.lessonItems.data[index], ...updates };
      }
    },
    deleteLessonItemOptimistic: (state, action) => {
      const { itemId } = action.payload;

      state.lessonItems.data = state.lessonItems.data.filter((item: any) => {
        const candidates = [
          getId(item),
          (item as any)?._id?.$oid,
          (item as any)?._id,
          (item as any)?.id,
          (item as any)?.data_id,
          (item as any)?.dataId,
        ]
          .filter(Boolean)
          .map(v => String(v));


        return !candidates.includes(String(itemId));
      });
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchElearningCategories.pending, state => {
      state.categories.loading = "pending";
      state.categories.error = null;
    });
    builder.addCase(fetchElearningCategories.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.categories.loading = "idle";
        return;
      }

      state.categories.loading = "failed";
      state.categories.error = action.payload;

      const now = Date.now();
      const timeSinceLastToast = now - (state.categories.lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        state.categories.lastErrorToastTime = now;
        toast.error("Failed to load categories. Please try again.", {
          toastId: "categories-load-error",
        });
      }
    });
    builder.addCase(fetchElearningCategories.fulfilled, (state, action: any) => {
      state.categories.filtered = action.payload?.data || [];
      state.categories.items = action.payload?.filtered || [];
      state.categories.total = action.payload?.total || 0;
      state.categories.loading = "succeeded";
    });

    // Courses
    builder.addCase(fetchElearningCourses.pending, state => {
      state.courses.loading = "pending";
      state.courses.error = null;
    });
    builder.addCase(fetchElearningCourses.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.courses.loading = "idle";
        return;
      }

      state.courses.loading = "failed";
      state.courses.error = action.payload || "Failed to load courses";

      const now = Date.now();
      const timeSinceLastToast = now - (state.courses.lastErrorToastTime || 0);

      if (timeSinceLastToast > 500) {
        state.courses.lastErrorToastTime = now;
        toast.error("Failed to load courses. Please try again.", {
          toastId: "courses-load-error",
        });
      }
    });
    builder.addCase(fetchElearningCourses.fulfilled, (state, action: any) => {
      state.courses.filtered = action.payload?.data || [];
      state.courses.items = action.payload?.filtered || [];
      state.courses.total = action.payload?.total || 0;
      state.courses.loading = "succeeded";
    });

    // Lessons
    builder.addCase(fetchCourseLessons.pending, state => {
      state.lessons.loading = "pending";
      state.lessons.error = null;
    });
    builder.addCase(fetchCourseLessons.fulfilled, (state, action) => {
      state.lessons.loading = "succeeded";
      state.lessons.data = action.payload.data || [];
    });
    builder.addCase(fetchCourseLessons.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.lessons.loading = "idle";
        return;
      }

      state.lessons.loading = "failed";
      state.lessons.error = (action.payload as string) || "Failed to load lessons";
    });

    // Delete lesson
    builder.addCase(deleteLesson.pending, state => {
      state.lessons.loading = "pending";
      state.lessons.error = null;
    });
    builder.addCase(deleteLesson.fulfilled, (state, action) => {
      state.lessons.loading = "succeeded";
      const { lessonId } = action.payload;

      if (lessonId) {
        state.lessons.data = state.lessons.data.filter((lesson: any) => {
          const id = typeof lesson._id === "object" ? lesson._id.$oid : lesson._id;


          return id !== lessonId;
        });
      }
    });
    builder.addCase(deleteLesson.rejected, (state, action) => {
      state.lessons.loading = "failed";
      state.lessons.error = (action.payload as string) || "Failed to delete lesson";
    });

    // Reset lesson (single user)
    builder.addCase(resetLesson.pending, state => {
      state.lessons.loading = "pending";
      state.lessons.error = null;
    });
    builder.addCase(resetLesson.fulfilled, state => {
      state.lessons.loading = "succeeded";
    });
    builder.addCase(resetLesson.rejected, (state, action) => {
      state.lessons.loading = "failed";
      state.lessons.error = (action.payload as string) || "Failed to reset lesson";
    });

    // Reset lesson for all
    builder.addCase(resetLessonForAll.pending, state => {
      state.lessons.loading = "pending";
      state.lessons.error = null;
    });
    builder.addCase(resetLessonForAll.fulfilled, state => {
      state.lessons.loading = "succeeded";
    });
    builder.addCase(resetLessonForAll.rejected, (state, action) => {
      state.lessons.loading = "failed";
      state.lessons.error = (action.payload as string) || "Failed to reset lesson for all";
    });
  },
});

export const fetchElearningCategories = createAsyncThunk("manageElearning/categories/fetch", async (_, { rejectWithValue }) => {
  try {
    const data = await doGetElearningCategories();

    if (data === null || data === undefined) {
      return rejectWithValue("Failed to load categories");
    }

    const categories = Array.isArray(data) ? data : [];


    return { data: categories, filtered: categories, total: categories.length };
  } catch (error) {
    return rejectWithValue("Failed to load categories");
  }
});

export const fetchElearningCourses = createAsyncThunk("manageElearning/courses/fetch", async (_, { rejectWithValue }) => {
  try {
    const data = await doGetElearningCoursesManage();

    if (data === null || data === undefined) {
      return rejectWithValue("Failed to load courses");
    }

    const courses = Array.isArray(data) ? data : [];


    return { data: courses, filtered: courses, total: courses.length };
  } catch (error) {
    return rejectWithValue("Failed to load courses");
  }
});

export const fetchCourseLessons = createAsyncThunk<{ data: any[] }, { courseId: string }, { rejectValue: string }>(
  "manageElearning/lessons/fetch",
  async ({ courseId }, { rejectWithValue }) => {
    try {
      const response = await doGetElearningCourseByIdManage(courseId);
      const lessons = (response as any)?.lessons || [];


      return { data: lessons };
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to fetch lessons";


      return rejectWithValue(error);
    }
  }
);

export const deleteLesson = createAsyncThunk<{ lessonId: string }, { courseId: string; lessonId: string }, { rejectValue: string }>(
  "manageElearning/lessons/delete",
  async ({ courseId, lessonId }, { rejectWithValue }) => {
    try {
      await doDeleteElearningLesson(courseId, lessonId);

      return { lessonId };
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to delete lesson";


      return rejectWithValue(error);
    }
  }
);

export const resetLesson = createAsyncThunk<{ success: boolean }, { courseId: string; lessonId: string; userId: string }, { rejectValue: string }>(
  "manageElearning/lessons/reset",
  async ({ courseId, lessonId, userId }, { rejectWithValue, dispatch }) => {
    try {
      await doResetElearningLesson(courseId, lessonId, userId);
      await dispatch(fetchCourseLessons({ courseId }));

      return { success: true };
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to reset lesson";


      return rejectWithValue(error);
    }
  }
);

export const resetLessonForAll = createAsyncThunk<{ success: boolean }, { courseId: string; lessonId: string }, { rejectValue: string }>(
  "manageElearning/lessons/resetAll",
  async ({ courseId, lessonId }, { rejectWithValue, dispatch }) => {
    try {
      await doResetElearningLessonForAll(courseId, lessonId);
      await dispatch(fetchCourseLessons({ courseId }));

      return { success: true };
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to reset lesson for all users";


      return rejectWithValue(error);
    }
  }
);

export const {
  filterCategories,
  filterCourses,
  clearLessons,
  setLessonItems,
  clearLessonItems,
  updateLessonItemsOrder,
  addLessonItemOptimistic,
  updateLessonItemOptimistic,
  deleteLessonItemOptimistic,
} = manageElearningSlice.actions;

export default manageElearningSlice.reducer;
